import React, { useEffect, useState } from "react";
import { Box, Collapse, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { BrandMark, IconChevronDown, IconLogout, IconSidebarToggle } from "../ui/icons";
import { adminNavSections } from "./navConfig";
import { getInitials } from "../../utils/initials";
import ThemeSwitch from "./ThemeSwitch";

const GROUPS_KEY = "sup.sidebarGroups";

const isPathActive = (item, pathname) => {
  if (item.path) return item.path === pathname;
  return (item.children || []).some((child) => child.path === pathname);
};

/**
 * Menú lateral grafito. Completo a partir de 1280 px; a partir de 768 px
 * se reduce a un riel de íconos con la etiqueta en tooltip.
 */
const Sidebar = ({
  collapsed = false,
  username = "",
  roleLabel = "Administrador",
  sections = adminNavSections,
  canToggle = false,
  onToggle,
  onLogout,
  badges = {},
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState(null);
  const [menuItems, setMenuItems] = useState([]);

  /**
   * Los submenús arrancan desplegados. Sólo se guardan los que el usuario
   * cierra a mano: `false` = cerrado, ausente = abierto.
   */
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(GROUPS_KEY) || "null");
      return saved && typeof saved === "object" ? saved : {};
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const isGroupOpen = (id) => openGroups[id] !== false;

  const go = (path) => {
    localStorage.removeItem("signatureData");
    navigate(path);
    setAnchor(null);
  };

  const handleItemClick = (event, item) => {
    if (item.path) return go(item.path);
    if (collapsed) {
      setMenuItems(item.children || []);
      setAnchor(event.currentTarget);
      return;
    }
    setOpenGroups((prev) => ({ ...prev, [item.id]: prev[item.id] === false }));
  };

  const width = collapsed ? tokens.railWidth : tokens.sidebarWidth;

  return (
    <Box
      component="nav"
      aria-label="Navegación principal"
      sx={{
        width,
        height: "100%",
        flexShrink: 0,
        backgroundColor: tokens.graphite,
        display: "flex",
        flexDirection: "column",
        padding: collapsed ? "18px 10px 14px" : "18px 14px 14px",
        overflowY: "auto",
        transition: "width 160ms ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: collapsed ? "0 0 18px" : "0 6px 18px",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <Box
          onClick={() => go(sections[0].items[0].path)}
          sx={{ display: "contents", cursor: "pointer" }}
        >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BrandMark size={17} />
        </Box>
        {!collapsed && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#fff", letterSpacing: "0.06em" }}
            >
              SUPPORT IT
            </Typography>
            <Typography sx={{ fontSize: "11px", color: tokens.graphiteInk3 }}>
              Gestión de servicios
            </Typography>
          </Box>
        )}
        </Box>

        {canToggle && !collapsed && (
          <Tooltip title="Colapsar menú" placement="right" arrow>
            <Box
              onClick={onToggle}
              aria-label="Colapsar menú"
              sx={{
                width: 30,
                height: 30,
                marginLeft: "auto",
                borderRadius: "7px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: tokens.graphiteInk3,
                cursor: "pointer",
                flexShrink: 0,
                "&:hover": { backgroundColor: tokens.graphite3, color: tokens.graphiteInk },
              }}
            >
              <IconSidebarToggle size={16} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {canToggle && collapsed && (
        <Tooltip title="Expandir menú" placement="right" arrow>
          <Box
            onClick={onToggle}
            aria-label="Expandir menú"
            sx={{
              width: 38,
              height: 38,
              margin: "0 auto 8px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: tokens.graphiteInk3,
              cursor: "pointer",
              "&:hover": { backgroundColor: tokens.graphite3, color: tokens.graphiteInk },
            }}
          >
            <IconSidebarToggle size={16} />
          </Box>
        </Tooltip>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {sections.map((section) => (
          <React.Fragment key={section.label}>
            {collapsed ? (
              <Box sx={{ height: "1px", backgroundColor: tokens.graphiteLine, margin: "10px 6px" }} />
            ) : (
              <Typography
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 600,
                  color: "#6a6461",
                  letterSpacing: "0.1em",
                  padding: "14px 10px 6px",
                  textTransform: "uppercase",
                }}
              >
                {section.label}
              </Typography>
            )}

            {section.items.map((item) => {
              const active = isPathActive(item, pathname);
              const Icon = item.icon;
              const badge = badges[item.id];
              const row = (
                <Box
                  key={item.id}
                  onClick={(e) => handleItemClick(e, item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    minHeight: 38,
                    padding: collapsed ? "9px 0" : "9px 10px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: active ? "#ffffff" : tokens.graphiteInk2,
                    backgroundColor: active ? tokens.graphite2 : "transparent",
                    "&:hover": { backgroundColor: active ? tokens.graphite2 : tokens.graphite3 },
                  }}
                >
                  {!collapsed && active && (
                    <Box
                      sx={{
                        width: "3px",
                        height: "15px",
                        borderRadius: "2px",
                        backgroundColor: tokens.brand,
                        flexShrink: 0,
                        marginLeft: "-4px",
                      }}
                    />
                  )}
                  <Icon size={17} />
                  {!collapsed && (
                    <Typography
                      sx={{ fontSize: "13.5px", fontWeight: active ? 500 : 400, flexGrow: 1 }}
                    >
                      {item.label}
                    </Typography>
                  )}
                  {!collapsed && badge > 0 && (
                    <Box
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        borderRadius: 999,
                        padding: "2px 7px",
                        fontVariantNumeric: "tabular-nums",
                        color: "#fff",
                        backgroundColor: item.id === "facturacion" ? tokens.brandDark : tokens.graphiteBadge,
                      }}
                    >
                      {badge}
                    </Box>
                  )}
                  {!collapsed && item.children && (
                    <Box
                      sx={{
                        display: "flex",
                        transition: "transform 140ms ease",
                        transform: isGroupOpen(item.id) ? "rotate(0deg)" : "rotate(-90deg)",
                      }}
                    >
                      <IconChevronDown size={14} />
                    </Box>
                  )}
                </Box>
              );

              return (
                <React.Fragment key={item.id}>
                  {collapsed ? (
                    <Tooltip title={item.label} placement="right" arrow>
                      {row}
                    </Tooltip>
                  ) : (
                    row
                  )}

                  {!collapsed && item.children && (
                    <Collapse in={isGroupOpen(item.id)} unmountOnExit>
                      <Box
                        sx={{ display: "flex", flexDirection: "column", gap: "1px", paddingLeft: "27px" }}
                      >
                        {item.children.map((child) => {
                          const childActive = child.path === pathname;
                          return (
                            <Box
                              key={child.path}
                              onClick={() => go(child.path)}
                              sx={{
                                padding: "8px 10px",
                                borderRadius: "7px",
                                fontSize: "13px",
                                cursor: "pointer",
                                fontWeight: childActive ? 500 : 400,
                                color: childActive ? "#ffffff" : "#8d8783",
                                backgroundColor: childActive ? tokens.graphite3 : "transparent",
                                "&:hover": { color: "#cfc9c5" },
                              }}
                            >
                              {child.label}
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <ThemeSwitch collapsed={collapsed} />

      <Box
        sx={{
          borderTop: `1px solid ${tokens.graphiteLine}`,
          paddingTop: "12px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        {!collapsed && (
          <>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 999,
                backgroundColor: tokens.graphiteBadge,
                color: tokens.graphiteInk,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12.5px",
                fontWeight: 600,
                flexShrink: 0,
                textTransform: "uppercase",
              }}
            >
              {getInitials(username)}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: tokens.graphiteInk,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {username || "Usuario"}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: tokens.graphiteInk3 }}>
                {roleLabel}
              </Typography>
            </Box>
          </>
        )}
        <Tooltip title="Cerrar sesión" placement="right" arrow>
          <Box
            onClick={onLogout}
            aria-label="Cerrar sesión"
            sx={{
              width: 38,
              height: 38,
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: tokens.graphiteInk3,
              cursor: "pointer",
              "&:hover": { backgroundColor: tokens.graphite3, color: tokens.graphiteInk },
            }}
          >
            <IconLogout size={16} />
          </Box>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuItems.map((child) => (
          <MenuItem key={child.path} onClick={() => go(child.path)} sx={{ fontSize: "13.5px" }}>
            {child.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default Sidebar;
